// สถิติการเช็คชื่อ แยกเป็นรายวิชาที่สอน (ภาคศาสนา) — ครูผู้สอนศาสนา (pos 50)
//
// คู่ขนานกับภาคสามัญ (teaching/AttendanceStatistic.js + ListAttendanceStatistic.js)
// แต่รวมเป็นหน้าเดียว: เลือกวิชาจากช่องด้านบนแล้วตารางเปลี่ยนตาม แทนที่จะให้ครูกดผ่าน
// หน้ารายการก่อน — ครูภาคศาสนาสอนวิชาเดียวกันหลายห้อง การเทียบห้องต่อห้องคือสิ่งที่
// ทำบ่อยที่สุด ถ้าต้องถอยกลับหน้ารายการทุกครั้งจะเสียจังหวะ
//
// ปุ่ม "สถิติ" ในหน้ารายวิชาของฉันลิงก์มาที่ /islamic/attendance-statistic/:is_teachId
// ซึ่งเป็นหน้าเดียวกันที่เลือกวิชาให้แล้ว
//
// หนึ่งคอลัมน์ = การเช็คชื่อหนึ่งครั้ง (วันที่ + คาบ) ไม่ใช่หนึ่งวัน — วิชาหนึ่งลงได้
// หลายคาบในวันเดียวกัน ยุบเป็นวันเมื่อไหร่คาบที่สองจะหายไปเงียบ ๆ
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import * as XLSX from 'xlsx'
import axiosInstance from '../services/axiosInterceptor'
import { getUserId } from '../services/authorize'
import { useLoading } from '../contexts/LoadingContext'
import { useLanguage } from '../contexts/LanguageContext'
import { StudentName, studentDisplayName } from '../utils/studentName'
import th from '../locales/islamic/th'
import jawi from '../locales/islamic/jawi'
import {
  API, ATT_STATUS, StatTile, StatusDot, YearSelect,
  alertRequestError, roomLabel, thaiDate, useDarkTables, useStudyYears,
} from '../islamic-academic/academicShared'

const STATUS_KEYS = ATT_STATUS.map(s => s.key)
const STATUS_COLOR = ATT_STATUS.reduce((m, s) => ({ ...m, [s.key]: s.color }), {})

// ชื่อสถานะบนหน้าจอใช้ชุดเดียวกับหน้าเช็คชื่อ ไม่ใช่ของหมวดวิชาการ — ครูคนเดียวกัน
// เพิ่งกรอกจากหน้านั้นมา คำที่ใช้ต้องเป็นคำเดิม
const statusLabel = (t, key) => t.checkAttendance[`status${key[0].toUpperCase()}${key.slice(1)}`]

const emptyCounts = () => STATUS_KEYS.reduce((m, k) => ({ ...m, [k]: 0 }), {})

// อัตราเข้าเรียน = (มา + สาย) ÷ ครั้งที่ถูกเช็คจริง ไม่ใช่ ÷ ครั้งที่เช็คชื่อทั้งหมด
// คนที่ย้ายเข้ากลางปีถูกเช็คน้อยครั้งกว่าเพื่อน หารด้วยตัวเดียวกันจะได้เลขที่ต่ำเกินจริง
const rateOf = (c) => (c.checked > 0 ? Math.round(((c.present + c.late) / c.checked) * 100) : null)

const sessionKey = (s) => `${s.date}__${s.period_id}`
const dateShort = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : '')
const escHtml = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

export default function IslamicAttendanceStatistic() {
  const { is_teachId } = useParams()
  const navigate = useNavigate()
  const { setLoading } = useLoading()
  const { lang } = useLanguage()
  const t = lang === 'th' ? th : jawi
  useDarkTables()

  const { years, yearId, setYearId } = useStudyYears()
  const [subjects, setSubjects] = useState([])
  const [teachId, setTeachId] = useState(is_teachId || '')
  const [data, setData] = useState(null)

  // รายวิชาที่สอนในปีที่เลือก — ใช้เป็นตัวเลือกของ dropdown
  useEffect(() => {
    if (!yearId) return
    let alive = true
    axiosInstance.get(`${API}/islamic-teaching/mySubjects/${getUserId()}/${yearId}`)
      .then(res => { if (alive) setSubjects(Array.isArray(res.data) ? res.data : []) })
      .catch(err => { if (alive) { setSubjects([]); alertRequestError(t, err, t.common.error) } })
    return () => { alive = false }
  }, [yearId, t])

  // เปลี่ยนปีแล้ววิชาที่เลือกไว้เป็นของปีก่อน — ล้างทิ้ง ไม่ปล่อยให้ตารางค้างข้อมูลปีเก่า
  // ทั้งที่ช่องเลือกปีด้านบนบอกว่าเป็นอีกปีหนึ่ง
  useEffect(() => {
    if (!teachId || subjects.length === 0) return
    if (!subjects.some(s => String(s.is_teachId) === String(teachId))) {
      setTeachId('')
      setData(null)
      navigate('/islamic/attendance-statistic', { replace: true })
    }
  }, [subjects, teachId, navigate])

  useEffect(() => {
    if (!teachId) { setData(null); return }
    let alive = true
    setLoading(true)
    axiosInstance.get(`${API}/islamic-teaching/attendanceStatistic/${teachId}`)
      .then(res => { if (alive) setData(res.data) })
      .catch(err => { if (alive) { setData(null); alertRequestError(t, err, t.common.error) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [teachId, setLoading, t])

  const onPickSubject = useCallback((id) => {
    setTeachId(id)
    navigate(id ? `/islamic/attendance-statistic/${id}` : '/islamic/attendance-statistic', { replace: true })
  }, [navigate])

  const teaching = data?.teaching || null
  const sessions = useMemo(() => data?.sessions || [], [data])
  const students = useMemo(() => data?.students || [], [data])

  // สถานะรายคน × รายครั้ง + ยอดสะสมของแต่ละคน คิดรอบเดียวแล้วใช้ทั้งตาราง สรุป และไฟล์ที่ส่งออก
  const { cellMap, rows, totals } = useMemo(() => {
    const map = new Map()
    for (const a of data?.attendance || []) {
      map.set(`${a.is_study_id}__${a.date}__${a.period_id}`, a.status)
    }
    const totalCounts = { ...emptyCounts(), checked: 0 }
    const list = students.map(st => {
      const counts = { ...emptyCounts(), checked: 0 }
      for (const s of sessions) {
        const status = map.get(`${st.is_study_id}__${sessionKey(s)}`)
        if (!status) continue
        counts.checked += 1
        if (counts[status] !== undefined) counts[status] += 1
        totalCounts.checked += 1
        if (totalCounts[status] !== undefined) totalCounts[status] += 1
      }
      return { student: st, counts, rate: rateOf(counts) }
    })
    return { cellMap: map, rows: list, totals: totalCounts }
  }, [data, students, sessions])

  const avgRate = rateOf(totals)

  // ป้ายกำกับคนที่ไม่ได้อยู่ห้องนี้แล้ว — แถวยังอยู่เพราะเคยถูกเช็คชื่อไว้จริงก่อนออก/ย้าย
  // ซ่อนทิ้งเมื่อไหร่ ช่องที่เคยเช็คไว้จะหายไปโดยไม่มีคำอธิบาย
  const flagOf = useCallback((st) => {
    if (String(st.st_status) === '0') return { label: t.checkAttendance.statusWithdrawn, cls: 'table-danger', bg: '#f8d7da' }
    if (st.transfer_room_id) {
      const room = st.transfer_class ? `${st.transfer_class}/${st.transfer_room_no}` : ''
      return { label: room ? t.checkAttendance.transferredTo(room) : t.checkAttendance.statusTransferred, cls: 'table-warning', bg: '#fff3cd' }
    }
    return null
  }, [t])

  const headLabel = teaching
    ? `${teaching.isubject_name} · ${t.common.classNo(teaching.ic_class, teaching.ir_no)}`
    : ''

  // เอกสารที่สั่งพิมพ์เป็นภาษาไทยเสมอ ไม่ตามภาษาเมนู — เหมือนตารางจัดอันดับและหนังสือ
  // สัญญาของหมวดติดตามนักเรียน เพราะเป็นกระดาษที่ส่งต่อให้ฝ่ายอื่นอ่าน
  const printTitle = teaching
    ? `สถิติการเช็คชื่อ ชั้น ${teaching.ic_class}/${teaching.ir_no} วิชา ${teaching.isubject_name} (${teaching.isubject_isid1 || ''})`
    : 'สถิติการเช็คชื่อ'
  const yearLine = teaching ? `ปีการศึกษา ${teaching.year_year}` : ''
  const PRINT_STATUS_TH = { present: 'มา', late: 'สาย', leave: 'ลา', absent: 'ขาด' }

  const handlePrint = () => {
    const thS = 'border:1px solid #999;padding:2px 4px;background:#ddeeff;'
    const tdS = 'border:1px solid #999;padding:2px 4px;'
    const head = sessions.map(s =>
      `<th style="${thS}text-align:center;width:26px;"><span style="writing-mode:vertical-lr;display:inline-block;line-height:1;">${escHtml(dateShort(s.date))}</span></th>`
    ).join('')
    const body = rows.map(({ student, counts, rate }, i) => {
      const flag = flagOf(student)
      const rowBg = flag ? `background:${flag.bg};` : ''
      const cells = sessions.map(s => {
        const status = cellMap.get(`${student.is_study_id}__${sessionKey(s)}`)
        const fg = status === 'present' || status === 'absent' ? 'color:#fff;' : ''
        const bg = status ? `background:${STATUS_COLOR[status]};${fg}` : ''
        return `<td style="${tdS}text-align:center;${bg}">${escHtml(status ? PRINT_STATUS_TH[status] : '')}</td>`
      }).join('')
      const note = flag ? ` <span style="color:#b02a37;font-size:0.85em;">(${escHtml(flag.label)})</span>` : ''
      const countCells = STATUS_KEYS.map(k => `<td style="${tdS}text-align:center;${rowBg}">${counts[k] || ''}</td>`).join('')
      return `<tr>
        <td style="${tdS}text-align:center;${rowBg}">${i + 1}</td>
        <td style="${tdS}text-align:center;${rowBg}">${escHtml(student.st_idnumber)}</td>
        <td style="${tdS}${rowBg}">${escHtml(student.st_name)}${note}</td>
        ${countCells}
        <td style="${tdS}text-align:center;${rowBg}">${rate == null ? '-' : `${rate}%`}</td>
        ${cells}
      </tr>`
    }).join('')
    const emptyRow = `<tr><td colspan="${8 + sessions.length}" style="${tdS}text-align:center;">ไม่พบข้อมูล</td></tr>`
    const html = `<!DOCTYPE html><html lang="th"><head><meta charset="UTF-8"><title>${escHtml(printTitle)}</title>
      <style>
        body { font-family: 'Cordia New', Tahoma, Arial, sans-serif; font-size: 13px; padding: 10px; }
        table { border-collapse: collapse; width: 100%; }
      </style></head>
      <body onload="window.print()">
        <h3 style="text-align:center;margin-bottom:4px;">${escHtml(printTitle)}</h3>
        <div style="text-align:center;margin-bottom:12px;">${escHtml(yearLine)} · เช็คชื่อ ${sessions.length} ครั้ง</div>
        <table><thead><tr>
          <th style="${thS}width:36px;">ที่</th>
          <th style="${thS}width:86px;">รหัส</th>
          <th style="${thS}">ชื่อ-สกุล</th>
          <th style="${thS}width:32px;">มา</th>
          <th style="${thS}width:32px;">สาย</th>
          <th style="${thS}width:32px;">ลา</th>
          <th style="${thS}width:32px;">ขาด</th>
          <th style="${thS}width:44px;">ร้อยละ</th>
          ${head}
        </tr></thead><tbody>${body || emptyRow}</tbody></table>
      </body></html>`
    const win = window.open('', '_blank', 'width=1200,height=900')
    if (!win) return
    win.document.write(html)
    win.document.close()
  }

  const handleExcel = () => {
    const sheet = [
      [printTitle],
      [yearLine],
      [`${t.attendanceStat.sessions}: ${sessions.length}`],
      [],
      [
        t.attendanceStat.colNo, t.attendanceStat.colId, t.attendanceStat.colName,
        ...STATUS_KEYS.map(k => statusLabel(t, k)),
        t.attendanceStat.colRate,
        ...sessions.map(s => `${dateShort(s.date)} ${t.attendanceStat.periodShort(s.period_order)}`),
      ],
    ]
    rows.forEach(({ student, counts, rate }, i) => {
      const flag = flagOf(student)
      const name = studentDisplayName(student, lang)
      sheet.push([
        i + 1,
        student.st_idnumber,
        flag ? `${name} (${flag.label})` : name,
        ...STATUS_KEYS.map(k => counts[k]),
        rate == null ? '' : `${rate}%`,
        ...sessions.map(s => {
          const status = cellMap.get(`${student.is_study_id}__${sessionKey(s)}`)
          return status ? statusLabel(t, status) : ''
        }),
      ])
    })
    const ws = XLSX.utils.aoa_to_sheet(sheet)
    ws['!cols'] = [
      { wch: 5 }, { wch: 12 }, { wch: 30 },
      ...STATUS_KEYS.map(() => ({ wch: 6 })), { wch: 8 },
      ...sessions.map(() => ({ wch: 9 })),
    ]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'สถิติการเช็คชื่อ')
    XLSX.writeFile(wb, `สถิติการเช็คชื่อ_${teaching?.isubject_isid1 || teachId}.xlsx`)
  }

  // จัดกลุ่มตัวเลือกตามห้อง เพราะครูคนเดียวสอนวิชาเดียวกันหลายห้อง ชื่อวิชาล้วน ๆ
  // ในรายการยาว ๆ จะซ้ำกันจนเลือกผิดห้อง
  const optionGroups = useMemo(() => {
    const groups = new Map()
    for (const s of subjects) {
      if (!groups.has(s.ir_id)) groups.set(s.ir_id, { room: s, items: [] })
      groups.get(s.ir_id).items.push(s)
    }
    return [...groups.values()]
  }, [subjects])

  const genderLabel = (g) => (g === 'male' || g === 'm' ? t.common.male : t.common.female)

  return (
    <React.Fragment>
      <div className="mb-3">
        <h4 className="main-title mb-0">{t.attendanceStat.pageTitle}</h4>
        <p className="text-secondary mb-0 fs-sm">{headLabel || t.attendanceStat.pickHint}</p>
      </div>

      <Card className="card-one mb-3">
        <Card.Body className="p-3">
          <Row className="g-2 align-items-end">
            <YearSelect t={t} years={years} yearId={yearId} setYearId={setYearId} />
            <Col xs={12} md={6} lg={5}>
              <Form.Label className="fs-xs text-secondary mb-1">{t.attendanceStat.selectSubject}</Form.Label>
              <Form.Select size="sm" value={teachId} onChange={e => onPickSubject(e.target.value)}>
                <option value="">{t.attendanceStat.subjectPlaceholder}</option>
                {optionGroups.map(({ room, items }) => (
                  <optgroup key={room.ir_id} label={`${roomLabel(room)} · ${genderLabel(room.ir_gender)}`}>
                    {items.map(s => (
                      <option key={s.is_teachId} value={s.is_teachId}>
                        {s.isubject_isid1} — {s.isubject_name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} lg="auto" className="ms-lg-auto">
              <Button size="sm" variant="outline-secondary" className="me-1" onClick={handlePrint} disabled={sessions.length === 0}>
                <i className="ri-printer-line me-1" />{t.attendanceStat.printBtn}
              </Button>
              <Button size="sm" variant="outline-success" onClick={handleExcel} disabled={sessions.length === 0}>
                <i className="ri-file-excel-line me-1" />{t.attendanceStat.excelBtn}
              </Button>
            </Col>
          </Row>
          {subjects.length === 0 && (
            <div className="text-secondary fs-sm mt-2">{t.subjectList.noTeaching}</div>
          )}
        </Card.Body>
      </Card>

      {!teachId ? (
        <Card className="card-one">
          <Card.Body className="text-center text-secondary py-5">{t.attendanceStat.pickHint}</Card.Body>
        </Card>
      ) : sessions.length === 0 ? (
        <Card className="card-one">
          <Card.Body className="text-center text-secondary py-5">{t.attendanceStat.noSessions}</Card.Body>
        </Card>
      ) : (
        <React.Fragment>
          <Row className="g-2 mb-3">
            <Col xs={6} md={4} lg={2}>
              <StatTile label={t.attendanceStat.sessions} value={sessions.length} />
            </Col>
            <Col xs={6} md={4} lg={2}>
              <StatTile label={t.attendanceStat.students} value={students.length} />
            </Col>
            <Col xs={6} md={4} lg={2}>
              <StatTile label={t.attendanceStat.avgRate} value={avgRate == null ? '–' : `${avgRate}%`} sub={t.attendanceStat.rateHint} />
            </Col>
            {ATT_STATUS.map(s => (
              <Col xs={6} md={3} lg={{ span: 'auto' }} key={s.key} className="flex-lg-fill">
                <StatTile
                  label={statusLabel(t, s.key)}
                  value={totals[s.key]}
                  sub={totals.checked > 0 ? `${Math.round((totals[s.key] / totals.checked) * 100)}%` : null}
                  color={s.color}
                />
              </Col>
            ))}
          </Row>

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center flex-wrap gap-2">
              <span className="fw-semibold">{headLabel}</span>
              {teaching?.is_programCode && teaching.is_programCode !== 'GP' && (
                <Badge bg="info">{teaching.is_programCode}</Badge>
              )}
              <span className="ms-auto d-flex align-items-center flex-wrap gap-3 fs-xs text-secondary">
                {ATT_STATUS.map(s => (
                  <span key={s.key} className="d-inline-flex align-items-center gap-1">
                    <StatusDot color={s.color} size={8} />
                    {t.attendanceStat.short[s.key]} = {statusLabel(t, s.key)}
                  </span>
                ))}
              </span>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table bordered hover className="mb-0 text-nowrap fs-sm">
                  <thead className="table-secondary">
                    <tr>
                      <th className="text-center" style={{ width: 44 }}>{t.attendanceStat.colNo}</th>
                      <th className="text-center" style={{ width: 90 }}>{t.attendanceStat.colId}</th>
                      <th style={{ minWidth: 180 }}>{t.attendanceStat.colName}</th>
                      {ATT_STATUS.map(s => (
                        <th key={s.key} className="text-center" style={{ width: 40 }} title={statusLabel(t, s.key)}>
                          <StatusDot color={s.color} size={8} />
                        </th>
                      ))}
                      <th className="text-center" style={{ width: 62 }} title={t.attendanceStat.rateHint}>{t.attendanceStat.colRate}</th>
                      {sessions.map(s => (
                        <th
                          key={sessionKey(s)}
                          className="text-center p-1"
                          style={{ width: 30 }}
                          title={[
                            thaiDate(s.date, { long: true }),
                            `${t.attendanceStat.periodShort(s.period_order)}${s.period_begin ? ` ${s.period_begin}–${s.period_end || ''}` : ''}`,
                            s.topic || '',
                          ].filter(Boolean).join(' · ')}
                        >
                          <span className="d-inline-block" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', lineHeight: 1 }}>
                            {dateShort(s.date)}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ student, counts, rate }, i) => {
                      const flag = flagOf(student)
                      return (
                        <tr key={student.is_study_id} className={flag ? flag.cls : ''}>
                          <td className="text-center text-secondary">{i + 1}</td>
                          <td className="text-center">{student.st_idnumber}</td>
                          <td>
                            <StudentName student={student} lang={lang} />
                            {flag && <span className="badge bg-secondary ms-2 fw-normal" style={{ fontSize: '0.7rem' }}>{flag.label}</span>}
                          </td>
                          {STATUS_KEYS.map(k => (
                            <td key={k} className="text-center">{counts[k] || <span className="text-secondary">–</span>}</td>
                          ))}
                          <td className="text-center fw-semibold">{rate == null ? '–' : `${rate}%`}</td>
                          {sessions.map(s => {
                            const status = cellMap.get(`${student.is_study_id}__${sessionKey(s)}`)
                            // สีพื้นบอกสถานะได้เร็วกว่าอ่านตัวอักษร แต่เหลือง/ฟ้าคอนทราสต์ต่ำ
                            // เกินกว่าจะให้สีเป็นตัวบอกความหมายตัวเดียว ทุกช่องจึงมีอักษรย่อกำกับ
                            return (
                              <td
                                key={sessionKey(s)}
                                className="text-center p-1"
                                title={status ? statusLabel(t, status) : t.attendanceStat.notChecked}
                                style={status
                                  ? { background: STATUS_COLOR[status], color: status === 'late' || status === 'leave' ? '#111' : '#fff' }
                                  : undefined}
                              >
                                {status ? t.attendanceStat.short[status] : ''}
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}
